<template>
  <v-card outlined class="rounded-xl">
    <v-card-text class="pa-4">

      <!-- ── Header ──────────────────────────────────────────────────── -->
      <div class="d-flex align-center mb-2" style="gap:10px;">
        <div class="rounded-lg d-flex align-center justify-center flex-shrink-0" style="width:36px;height:36px;background:linear-gradient(135deg,#f97316,#f59e0b);">
          <v-icon color="white" size="18">mdi-repeat</v-icon>
        </div>
        <div class="flex-grow-1 d-flex align-baseline flex-wrap" style="min-width:0;gap:8px;">
          <div class="font-weight-bold" style="font-size:16px;line-height:1.2;white-space:nowrap;">Habit Tracker</div>
          <div class="text-truncate" style="font-size:13px;line-height:1.2;font-style:italic;font-weight:600;color:#f59e0b;letter-spacing:0.01em;">
            Bet on yourself today
          </div>
        </div>
        <div class="d-flex align-center flex-shrink-0" style="gap:6px;">
          <v-btn icon x-small :title="compact ? 'Switch to spacious' : 'Switch to compact'" @click="compact = !compact; saveCompact()">
            <v-icon size="15" class="grey--text">{{ compact ? 'mdi-view-agenda-outline' : 'mdi-view-sequential-outline' }}</v-icon>
          </v-btn>
          <v-btn x-small depressed rounded color="primary" class="text-capitalize font-weight-bold" :loading="aiSuggesting" :disabled="aiSuggesting" @click="addAiHabit" style="min-height:28px;">
            <v-icon x-small class="mr-1">mdi-shimmer</v-icon>AI Suggest
          </v-btn>
          <v-btn depressed color="primary" class="white--text rounded-lg" style="min-width:30px;width:30px;height:30px;padding:0;" @click="addingHabit = !addingHabit">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Legend + default goal on its own row -->
      <div class="legend-row mb-3">
        <span class="d-inline-flex align-center" style="gap:5px;font-size:13px;"><img src="/emoji/1f60d.svg" style="width:22px;height:22px;"/> excellent</span>
        <span class="d-inline-flex align-center" style="gap:5px;font-size:13px;"><img src="/emoji/1f60a.svg" style="width:22px;height:22px;"/> good</span>
        <span class="d-inline-flex align-center" style="gap:5px;font-size:13px;"><img src="/emoji/1f622.svg" style="width:22px;height:22px;"/> poor</span>
        <span class="d-inline-flex align-center" style="gap:5px;font-size:13px;"><img src="/emoji/1f634.svg" style="width:22px;height:22px;"/> inactive</span>
        <span class="d-inline-flex align-center" style="gap:5px;font-size:13px;"><span class="hollow-circle" style="width:13px;height:13px;"></span> upcoming</span>
        <span class="default-goal d-inline-flex align-center" style="gap:4px;">
          <v-icon size="11" color="primary">mdi-target</v-icon>
          <span>Default</span>
          <input type="number" min="1" max="23" :value="defaultGoal" @change="onDefaultGoalChange"
            style="width:42px;border:1px solid #ccc;border-radius:4px;padding:0 4px;text-align:center;font-size:10px;background:white;" />
          <span>d</span>
        </span>
      </div>

      <!-- ── Habit count + year-wide aggregate emoji row ─────────────────── -->
      <div v-if="habits.length > 0" class="rounded-lg pa-2 mb-3 d-flex align-center" style="background:rgba(0,0,0,0.04);border:1px solid rgba(0,0,0,0.08);gap:12px;">
        <div class="font-weight-bold" style="font-size:13px;white-space:nowrap;">
          <span class="grey--text">Habit Count: </span>
          <span class="primary--text">{{ doneToday }}</span>
          <span class="grey--text">/{{ habits.length }} done today</span>
        </div>
        <div class="ml-auto d-flex flex-column align-end" style="gap:2px;min-width:0;flex:1;">
          <div class="d-flex align-center" style="gap:4px;width:100%;" aria-label="Yearly overall status">
            <v-tooltip
              v-for="(m, i) in headerMonthStatuses"
              :key="i"
              top
              max-width="260"
            >
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-flex align-center justify-center" style="flex:1;min-width:0;cursor:help;">
                  <span class="month-emoji-cell" style="line-height:1;display:inline-flex;align-items:center;justify-content:center;">
                    <template v-if="m.status === 'upcoming'">
                      <span class="hollow-circle" style="width:14px;height:14px;"></span>
                    </template>
                    <template v-else><img :src="statusEmojiUrl(m.status)" style="width:22px;height:22px;object-fit:contain;" /></template>
                  </span>
                </div>
              </template>
              <div class="font-weight-bold">{{ monthFullNames[i] }}</div>
              <div style="font-size:11px;opacity:0.85;">{{ headerMonthTooltip(m) }}</div>
            </v-tooltip>
          </div>
          <div class="d-flex align-center" style="gap:4px;width:100%;">
            <span
              v-for="(l, i) in monthLetters"
              :key="'l'+i"
              :style="{
                flex: '1',
                textAlign: 'center',
                fontSize: '9px',
                lineHeight: '1',
                fontWeight: 'bold',
                color: i === curMonth ? '#6366f1' : 'rgba(0,0,0,0.45)'
              }"
            >{{ l }}</span>
          </div>
        </div>
      </div>

      <!-- ── Add habit input ────────────────────────────────────────── -->
      <v-expand-transition>
        <div v-if="addingHabit" class="d-flex align-center mb-3" style="gap:8px;">
          <v-text-field v-model="newHabit" placeholder="Add a daily habit..." dense outlined hide-details autofocus class="flex-grow-1" @keyup.enter="addHabit" />
          <v-btn small depressed color="primary" class="white--text rounded-lg text-capitalize" @click="addHabit">Add</v-btn>
        </div>
      </v-expand-transition>

      <!-- ── AI pending preview ──────────────────────────────────────── -->
      <v-expand-transition>
        <div v-if="pendingHabit" class="pa-3 rounded-lg mb-3" style="background:#eff6ff;border:1px solid #93c5fd;">
          <div class="caption font-weight-bold mb-1 grey--text text--darken-1" style="letter-spacing:.08em;font-size:11px;">AI SUGGESTED HABIT</div>
          <div class="body-2 font-weight-medium mb-2" style="font-size:14px;">{{ pendingHabit }}</div>
          <div class="d-flex align-center" style="gap:8px;">
            <v-btn x-small depressed rounded color="primary" class="white--text text-capitalize" @click="confirmPendingHabit">
              <v-icon x-small class="mr-1">mdi-check</v-icon>Add habit
            </v-btn>
            <v-btn x-small depressed rounded color="blue lighten-3" class="white--text text-capitalize" :loading="aiSuggesting" @click="regenPendingHabit">
              <v-icon x-small class="mr-1">mdi-refresh</v-icon>Generate new
            </v-btn>
            <v-btn x-small text class="text-capitalize grey--text" @click="pendingHabit = null">× Dismiss</v-btn>
          </div>
        </div>
      </v-expand-transition>

      <!-- ── Empty state ────────────────────────────────────────────── -->
      <div v-if="habits.length === 0 && !loading" class="body-2 grey--text text-center py-4">
        No habits yet — add one or let AI suggest
      </div>

      <!-- ── Habit list ─────────────────────────────────────────────── -->
      <div :style="{ display:'flex', flexDirection:'column', gap: compact ? '4px' : '8px' }">
        <div
          v-for="h in habits"
          :key="h.id"
          class="rounded-lg group-row"
          :class="h.completedToday ? 'completed-row' : 'default-row'"
          :style="compact ? 'padding:6px 8px;' : 'padding:10px 12px;'"
        >
          <!-- Main row -->
          <div class="d-flex align-center" :style="{ gap: compact ? '8px' : '12px' }">

            <!-- Today toggle emoji (🫡 done · 😑 not done) -->
            <button
              type="button"
              :disabled="!!savingId"
              :title="h.completedToday ? 'Done today 🫡 — click to undo' : 'Mark as done today'"
              :aria-label="h.completedToday ? 'Mark as not done today' : 'Mark as done today'"
              @click="toggleToday(h)"
              class="emoji-status-btn flex-shrink-0"
              :style="{ background:'none', border:'none', cursor: savingId ? 'wait':'pointer', padding:'0', display:'inline-flex', alignItems:'center', justifyContent:'center', width:'28px', height:'28px', fontSize:'22px', lineHeight:'1', overflow:'visible' }"
            >
              <img v-if="h.completedToday" src="/emoji/1fae1.svg" alt="🫡" style="width:26px;height:26px;object-fit:contain;" />
              <img v-else src="/emoji/1f611.svg" alt="😑" style="width:26px;height:26px;object-fit:contain;" />
            </button>

            <!-- Text + badges (name itself opens calendar) -->
            <div class="flex-grow-1" style="min-width:0;">
              <v-tooltip top max-width="420">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    v-bind="attrs"
                    v-on="on"
                    @click="openHabitDetail(h)"
                    class="habit-name-btn"
                    :style="{ fontSize: compact ? '13px':'15px', lineHeight: compact ? '1.3':'1.4', textDecoration: h.completedToday ? 'line-through':'none', opacity: h.completedToday ? 0.55 : 1 }"
                  >
                    {{ h.text }}
                  </button>
                </template>
                <span>{{ h.text }}</span>
              </v-tooltip>
            </div>

            <!-- Status chip: on track / barely / behind -->
            <span class="status-chip flex-shrink-0" :class="statusChipClass(h)" style="font-size:10px;padding:2px 8px;">
              {{ statusChipLabel(h) }}
            </span>

            <!-- Time-of-day toggle (AM / WORK / PM) -->
            <button
              type="button"
              class="tod-badge flex-shrink-0"
              :class="todClass(h)"
              :title="`Time-of-day: ${todValue(h)} — click to cycle`"
              @click.stop="cycleTimeOfDay(h)"
            >
              {{ todValue(h) }}
            </button>

            <!-- Mark / Done button — only in expanded mode -->
            <button
              v-if="!compact"
              type="button"
              :disabled="!!savingId || h.completedToday"
              @click="markToday(h)"
              class="mark-btn flex-shrink-0 mark-btn-spacious"
              :class="h.completedToday ? 'mark-btn-done' : 'mark-btn-todo'"
            >
              {{ h.completedToday ? 'Done ✓' : 'Mark today' }}
            </button>

            <!-- Delete -->
            <button type="button" @click="removeHabit(h.id)" class="delete-btn flex-shrink-0" title="Delete habit">
              <v-icon :size="compact ? 12 : 14" class="grey--text">mdi-delete-outline</v-icon>
            </button>
          </div>

          <!-- Progress + 12-month strip row -->
          <div :style="{ marginTop: compact ? '4px':'8px', paddingLeft: compact ? '0':'60px' }">
            <div class="d-flex align-center" style="gap:8px;flex-wrap:nowrap;">
              <!-- Stats -->
              <div class="d-flex align-center flex-shrink-0" style="gap:6px;font-size:12px;font-weight:600;white-space:nowrap;">
                <span :style="{ color: monthPctFor(h) >= 100 ? '#22c55e':'#6366f1' }">{{ monthDoneFor(h) }}/{{ habitTarget(h) }}</span>
                <span style="color:#f59e0b;display:inline-flex;align-items:center;gap:2px;"><img src="/emoji/1f525.svg" style="width:12px;height:12px;"/>{{ computedStreak(h) }}d</span>
              </div>
              <!-- Spacious: per-habit goal input -->
              <div v-if="!compact" class="d-flex align-center flex-shrink-0 grey--text" style="gap:4px;font-size:12px;">
                <span>Goal:</span>
                <input type="number" min="1" max="23" :value="habitTarget(h)" @change="e => setHabitTarget(h, Number(e.target.value))"
                  style="width:48px;border:1px solid #ccc;border-radius:4px;padding:0 4px;text-align:center;font-size:12px;" />
              </div>
              <!-- 12-month emoji strip -->
              <div
                class="d-flex flex-grow-1"
                style="gap:4px;min-width:0;"
                aria-label="12-month performance"
              >
                <v-tooltip
                  v-for="(ms, i) in monthStatusesFor(h)"
                  :key="i"
                  top
                  max-width="260"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      class="d-flex align-center justify-center"
                      style="flex:1;min-width:0;cursor:help;"
                    >
                      <span class="month-emoji-cell" style="line-height:1;display:inline-flex;align-items:center;justify-content:center;">
                        <template v-if="ms.status === 'upcoming'">
                          <span class="hollow-circle" style="width:14px;height:14px;"></span>
                        </template>
                        <template v-else><img :src="statusEmojiUrl(ms.status)" style="width:22px;height:22px;object-fit:contain;" /></template>
                      </span>
                    </div>
                  </template>
                  <div class="font-weight-bold">{{ h.text }} — {{ monthFullNames[i] }}</div>
                  <div style="font-size:11px;opacity:0.85;">{{ monthStatusTooltip(ms) }}</div>
                </v-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

    </v-card-text>

    <!-- ── Habit detail / calendar dialog ─────────────────────────────── -->
    <v-dialog v-model="detailDialog" max-width="460" @click:outside="closeHabitDetail">
      <v-card v-if="detailHabit" class="rounded-xl">
        <v-card-title style="position:relative;padding-right:44px;">
          <div style="min-width:0;width:100%;">
            <div class="font-weight-bold" style="font-size:16px;line-height:1.3;white-space:normal;word-break:break-word;">{{ detailHabit.text }}</div>
            <div class="caption grey--text">
              {{ detailHabit.source === 'ai' ? 'AI suggested habit' : 'Manual habit' }} · workdays only
            </div>
          </div>
          <v-btn icon small @click="closeHabitDetail" style="position:absolute;top:8px;right:8px;"><v-icon size="18">mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Header: done / target -->
          <div class="d-flex align-center justify-space-between flex-wrap mb-3" style="gap:8px;">
            <div style="font-size:15px;font-weight:bold;">
              <span :style="{ color: monthDoneFor(detailHabit) >= habitTarget(detailHabit) ? '#22c55e' : '#6366f1' }">
                {{ monthDoneFor(detailHabit) }}
              </span>
              <span class="grey--text"> / {{ habitTarget(detailHabit) }}</span>
              <span class="caption grey--text font-weight-regular ml-1">days completed</span>
            </div>
            <div class="d-flex align-center" style="gap:6px;">
              <span class="streak-chip" style="display:inline-flex;align-items:center;gap:3px;"><img src="/emoji/1f525.svg" style="width:14px;height:14px;"/> {{ computedStreak(detailHabit) }}d</span>
              <span class="status-chip" :class="statusChipClass(detailHabit)">
                {{ statusChipLabel(detailHabit) }}
              </span>
            </div>
          </div>

          <!-- Calendar grid -->
          <div class="mb-3">
            <div class="d-flex" style="gap:4px;">
              <div v-for="(w, i) in weekdayLabels" :key="'wd-'+i" class="weekday-label">{{ w }}</div>
            </div>
            <div class="calendar-grid mt-1">
              <div v-for="n in detailLeadingBlanks" :key="'b-'+n" class="cal-blank"></div>
              <button
                v-for="iso in detailMonthDays"
                :key="iso"
                type="button"
                :disabled="isCellDisabled(iso)"
                :class="cellClass(detailHabit, iso)"
                :title="iso"
                @click="toggleHabitDate(detailHabit, iso)"
              >
                <span v-if="isWeekendIso(iso) || isFutureIso(iso) || isTodayIso(iso)">{{ dayNumOf(iso) }}</span>
                <v-icon v-else-if="isDoneIso(detailHabit, iso)" size="14" color="#16a34a">mdi-check</v-icon>
                <v-icon v-else size="14" color="#dc2626">mdi-close</v-icon>
              </button>
            </div>
          </div>

          <!-- Footer: goal + delete -->
          <v-divider class="mb-3" />
          <div class="d-flex align-center justify-space-between" style="gap:8px;">
            <div class="d-flex align-center grey--text" style="gap:6px;font-size:12px;">
              <v-icon size="14" color="primary">mdi-target</v-icon>
              <label :for="`goal-input-${detailHabit.id}`">Goal</label>
              <input
                :id="`goal-input-${detailHabit.id}`"
                type="number"
                min="1"
                max="23"
                :value="habitTarget(detailHabit)"
                @change="e => setHabitTarget(detailHabit, Number(e.target.value))"
                style="width:52px;border:1px solid #ccc;border-radius:4px;padding:0 4px;text-align:center;font-size:12px;background:white;"
              />
              <span>days/month</span>
            </div>
            <v-btn text x-small color="grey darken-1" @click="confirmDeleteFromDetail">
              <v-icon size="14" left>mdi-delete-outline</v-icon>
              Delete
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { askAI, buildUserContext } from '@/services/growthAiService';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DEFAULT_MONTHLY_TARGET = 20;
const GOAL_KEY = 'employee-os-habits-default-goal';

const MILESTONES = [
  { days: 3,  label: 'Spark',     icon: 'mdi-lightning-bolt' },
  { days: 7,  label: 'Week',      icon: 'mdi-star' },
  { days: 14, label: 'Fortnight', icon: 'mdi-fire' },
  { days: 30, label: 'Champion',  icon: 'mdi-trophy' },
];

const ID_TO_PROFILE = {
  1:'superadmin',2:'management',3:'agent',4:'engineering',5:'engineering',9:'engineering',
  6:'marketing',7:'design',8:'sales',10:'recruitment',11:'management',12:'operations',13:'finance',
};
const PROFILE_PRIORITY = {
  superadmin:100,management:90,sales:70,agent:65,engineering:60,
  recruitment:55,marketing:50,design:45,operations:40,finance:35,generic:0,
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const isWorkday = (iso) => {
  const [y,m,d] = iso.split('-').map(Number);
  const day = new Date(y, m-1, d).getDay();
  return day !== 0 && day !== 6;
};

const computeStreak = (history) => {
  if (!history || !history.length) return 0;
  const set = new Set(history);
  let streak = 0;
  const d = new Date();
  if (!set.has(todayISO())) d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (set.has(key)) {
      streak++;
      d.setDate(d.getDate() - 1);
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    } else break;
  }
  return streak;
};

const monthlyCountsForYear = (history, year) => {
  const counts = new Array(12).fill(0);
  for (const iso of (history || [])) {
    if (!isWorkday(iso)) continue;
    const [y, m] = iso.split('-');
    if (Number(y) === year) { const idx = Number(m)-1; if (idx >= 0 && idx < 12) counts[idx]++; }
  }
  return counts;
};

const computeMonthStreak = (history, target) => {
  const now = new Date();
  const curMonth = now.getMonth();
  let y = now.getFullYear();
  let yearCounts = monthlyCountsForYear(history, y);
  let monthStreak = 0;
  let idx = yearCounts[curMonth] >= target ? curMonth : curMonth - 1;
  while (idx >= 0 ? yearCounts[idx] >= target : false) {
    monthStreak++;
    idx--;
    if (idx < 0) {
      y -= 1;
      yearCounts = monthlyCountsForYear(history, y);
      idx = 11;
      if (yearCounts.every(c => c === 0)) break;
    }
  }
  return monthStreak;
};

const currentMonthWorkdays = () => {
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth();
  const total = new Date(year, month+1, 0).getDate();
  const days = [];
  for (let d = 1; d <= total; d++) {
    const iso = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    if (isWorkday(iso)) days.push(iso);
  }
  return days;
};

export default {
  name: 'HabitTrackerCard',
  inject: {
    personalTasks: { default: () => () => [] },
    assignedTasks: { default: () => () => [] },
    allManagers:   { default: () => () => [] },
    roles:         { default: () => () => [] },
  },
  data() {
    const rawGoal = localStorage.getItem(GOAL_KEY);
    const n = rawGoal ? Number(rawGoal) : DEFAULT_MONTHLY_TARGET;
    const compactRaw = localStorage.getItem('employee-os-habits-compact');
    return {
      habits: [],
      loading: false,
      aiSuggesting: false,
      addingHabit: false,
      newHabit: '',
      pendingHabit: null,
      savingId: null,
      defaultGoal: Number.isFinite(n) && n >= 1 && n <= 23 ? n : DEFAULT_MONTHLY_TARGET,
      compact: compactRaw === null ? true : compactRaw === '1',
      kpis: [],
      growthAreas: [],
      monthNames: MONTH_NAMES,
      monthFullNames: ['January','February','March','April','May','June','July','August','September','October','November','December'],
      monthLetters: ['J','F','M','A','M','J','J','A','S','O','N','D'],
      curMonth: new Date().getMonth(),
      curYear: new Date().getFullYear(),
      // Habit detail / calendar dialog state
      detailDialog: false,
      detailHabitId: null,
      weekdayLabels: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    };
  },
  computed: {
    storeUser() { return this.$store.state.auth?.user || {}; },
    userRoleIds() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.id).filter(id => id != null);
    },
    userRoleNames() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.name).filter(Boolean);
    },
    managedRoleIds() {
      const uid = this.storeUser.id;
      const managers = typeof this.allManagers === 'function' ? this.allManagers() : [];
      if (!Array.isArray(managers) || !uid) return [];
      return managers.filter(m => m.user_id === uid).map(m => m.user_role_id).filter(id => id != null);
    },
    managedRoleNames() {
      const rolesList = typeof this.roles === 'function' ? this.roles() : [];
      if (!Array.isArray(rolesList)) return [];
      return this.managedRoleIds.map(id => rolesList.find(r => r.id === id)?.name).filter(Boolean);
    },
    roleProfile() {
      const ids = new Set(this.userRoleIds);
      const legacy = this.storeUser.role_id;
      if (legacy === 1) ids.add(1); if (legacy === 11) ids.add(11); if (legacy === 2) ids.add(2);
      const profiles = new Set();
      ids.forEach(id => { const p = ID_TO_PROFILE[id]; if (p) profiles.add(p); });
      if (this.managedRoleIds.length > 0) profiles.add('management');
      const arr = Array.from(profiles).sort((a,b) => (PROFILE_PRIORITY[b]||0)-(PROFILE_PRIORITY[a]||0));
      return arr[0] || 'generic';
    },
    primaryRoleName() {
      return this.userRoleNames[0] || (this.storeUser.role_id === 1 || this.storeUser.role_id === 11
        ? 'Admin' : this.storeUser.role_id === 2 ? 'Manager' : 'Employee');
    },
    allTasks() {
      const p = typeof this.personalTasks === 'function' ? this.personalTasks() : [];
      const a = typeof this.assignedTasks === 'function' ? this.assignedTasks() : [];
      return [...p, ...a].filter(t => !t.deleted_at);
    },
    overdueCount() {
      const today = new Date().toISOString().slice(0,10);
      return this.allTasks.filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0,10) < today).length;
    },
    completedThisWeekCount() {
      const weekAgo = new Date(Date.now()-7*24*3600*1000).toISOString().slice(0,10);
      return this.allTasks.filter(t => t.completed_at && String(t.completed_at).slice(0,10) >= weekAgo).length;
    },
    inProgressCount() {
      return this.allTasks.filter(t => !t.completed_at && (t.status?.name||'').toLowerCase().includes('progress')).length;
    },
    pendingKpiCount() { return (this.kpis||[]).filter(k => !k.is_done).length; },
    longestStreak() { return this.habits.reduce((m,h) => Math.max(m, this.computedStreak(h)), 0); },
    stalledHabits() { return this.habits.filter(h => this.computedStreak(h) === 0 && !h.completedToday).map(h => h.text); },
    doneToday() { return this.habits.filter(h => h.completedToday).length; },
    todayPct() {
      if (!this.habits.length) return 0;
      return Math.round((this.doneToday / this.habits.length) * 100);
    },
    totalMonthDone() {
      const workdays = currentMonthWorkdays();
      return this.habits.reduce((sum, h) => sum + workdays.filter(d => (h.history||[]).includes(d)).length, 0);
    },
    totalMonthTarget() {
      return this.habits.reduce((sum, h) => sum + this.habitTarget(h), 0);
    },
    monthPct() {
      if (!this.totalMonthTarget) return 0;
      return Math.min(100, Math.round((this.totalMonthDone/this.totalMonthTarget)*100));
    },
    detailHabit() {
      if (!this.detailHabitId) return null;
      return this.habits.find(h => h.id === this.detailHabitId) || null;
    },
    detailMonthDays() {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const total = new Date(y, m + 1, 0).getDate();
      const days = [];
      for (let d = 1; d <= total; d++) {
        days.push(`${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
      }
      return days;
    },
    detailLeadingBlanks() {
      // Monday-first calendar: blanks before the 1st of the month
      const first = this.detailMonthDays[0];
      if (!first) return 0;
      const [y, m, d] = first.split('-').map(Number);
      const dow = new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
      return (dow + 6) % 7;
    },
    // Aggregate yearly status per month across ALL habits (header strip)
    headerMonthStatuses() {
      const total = this.habits.length;
      if (!total) {
        return Array.from({ length: 12 }, () => ({ status: 'upcoming', excellent: 0, good: 0, total: 0 }));
      }
      const now = new Date();
      const curMonthIdx = now.getMonth();
      const curYr = now.getFullYear();
      return Array.from({ length: 12 }, (_, m) => {
        let excellent = 0, good = 0, inactiveCount = 0, upcoming = 0;
        this.habits.forEach(h => {
          const s = (this.monthStatusesFor(h)[m] || {}).status;
          if (s === 'excellent') excellent++;
          else if (s === 'good') good++;
          else if (s === 'inactive') inactiveCount++;
          else if (s === 'upcoming') upcoming++;
        });
        let status;
        const isCurrentMonth = m === curMonthIdx && this.curYear === curYr;
        if (upcoming === total) status = 'upcoming';
        else if (inactiveCount === total) status = 'inactive';
        else if (excellent === total) status = 'excellent';
        else if (excellent + good >= Math.ceil(total / 2)) status = 'good';
        // For the current in-progress month, any positive activity = at least 'good'
        else if (isCurrentMonth && excellent + good > 0) status = 'good';
        else status = 'poor';
        return { status, excellent, good, total };
      });
    },
  },
  mounted() {
    this.load();
    this.loadSideContext();
    this.$root.$on('growth:refresh', this.onGrowthRefresh);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.onGrowthRefresh);
  },
  methods: {
    onGrowthRefresh() { this.load(); this.loadSideContext(); },
    saveCompact() { localStorage.setItem('employee-os-habits-compact', this.compact ? '1' : '0'); },
    onDefaultGoalChange(e) {
      const n = Math.max(1, Math.min(23, parseInt(e.target.value) || DEFAULT_MONTHLY_TARGET));
      this.defaultGoal = n;
      localStorage.setItem(GOAL_KEY, String(n));
    },

    // ── Per-habit helpers ─────────────────────────────────────────────
    computedStreak(h) { return computeStreak(h.history); },
    habitTarget(h)    { return h.monthly_target || this.defaultGoal; },
    yearCountsFor(h)  { return monthlyCountsForYear(h.history, this.curYear); },
    monthDoneFor(h) {
      const workdays = currentMonthWorkdays();
      return workdays.filter(d => (h.history||[]).includes(d)).length;
    },
    monthPctFor(h) {
      const t = this.habitTarget(h);
      return t > 0 ? Math.min(100, Math.round((this.monthDoneFor(h)/t)*100)) : 0;
    },
    monthStreakFor(h) { return computeMonthStreak(h.history||[], this.habitTarget(h)); },
    nextMilestoneFor(h) {
      const s = this.computedStreak(h);
      return MILESTONES.find(m => s < m.days) || MILESTONES[MILESTONES.length - 1];
    },
    currentTierFor(h) {
      const s = this.computedStreak(h);
      let tier = null;
      for (const m of MILESTONES) { if (s >= m.days) tier = m; }
      return tier;
    },
    monthTierLabel(m) {
      if (m >= 12) return 'Legend'; if (m >= 6) return 'Elite';
      if (m >= 3)  return 'Strong'; if (m >= 1) return 'Building';
      return '';
    },
    monthTierEmoji(m) {
      if (m >= 12) return '👑'; if (m >= 6) return '🏆';
      if (m >= 3)  return '🔥'; if (m >= 1) return '🏆';
      return '🏆';
    },
    monthTierClass(m) {
      if (m >= 12) return 'month-tier-legend'; if (m >= 6) return 'month-tier-elite';
      if (m >= 3)  return 'month-tier-strong';  if (m >= 1) return 'month-tier-building';
      return 'grey--text';
    },
    monthBarClass(count, target, monthIdx) {
      if (monthIdx > this.curMonth) return 'month-bar-future';
      if (count >= target) return 'month-bar-hit';
      const pct = target > 0 ? (count/target)*100 : 0;
      if (pct >= 50) return 'month-bar-warn';
      if (count > 0) return 'month-bar-partial';
      return 'month-bar-empty';
    },
    monthBarHeight(count, target, monthIdx) {
      if (monthIdx > this.curMonth) return '10%';
      const pct = target > 0 ? Math.min(100, (count/target)*100) : 0;
      const minH = this.compact ? 15 : 8;
      return `${Math.max(minH, pct)}%`;
    },

    // ── API ───────────────────────────────────────────────────────────
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/habits');
        if (res.data.response) {
          this.habits = res.data.data.map(h => ({
            ...h,
            history: h.history || [],
            completedToday: !!h.completed_today,
            monthly_target: h.monthly_target || this.defaultGoal,
          }));
        }
      } finally { this.loading = false; }
    },
    async loadSideContext() {
      try {
        const [kpis, areas] = await Promise.all([
          this.$axios.get('api/employee-growth/kpis'),
          this.$axios.get('api/employee-growth/areas'),
        ]);
        if (kpis.data?.response) {
          const p = kpis.data.data || {};
          this.kpis = Array.isArray(p) ? p : [...(p.this_week||[]),...(p.last_week||[])];
        }
        if (areas.data?.response) this.growthAreas = Array.isArray(areas.data.data) ? areas.data.data : [];
      } catch (e) { /* non-critical */ }
    },
    async addHabit() {
      const t = this.newHabit.trim();
      if (!t) return;
      try {
        const res = await this.$axios.post('api/employee-growth/habits', { text: t, source: 'manual', monthly_target: this.defaultGoal });
        if (res.data.response) {
          const h = res.data.data;
          this.habits.push({ ...h, history: h.history||[], completedToday: !!h.completed_today, monthly_target: h.monthly_target||this.defaultGoal });
          this.newHabit = ''; this.addingHabit = false;
        }
      } catch (e) { this.$toast?.error('Failed to add habit'); }
    },
    async markToday(h) {
      if (this.savingId || h.completedToday) return;
      this.savingId = h.id;
      const today = todayISO();
      // Optimistic
      h.history = [...(h.history||[]), today];
      h.completedToday = true;
      try {
        const res = await this.$axios.post(`api/employee-growth/habits/${h.id}/toggle`);
        if (res.data.response) {
          const u = res.data.data;
          h.history = u.history || h.history;
          h.streak = u.streak;
          h.completedToday = !!u.completed_today;
          this.$root.$emit('growth:refresh');
        } else {
          h.history = h.history.filter(d => d !== today);
          h.completedToday = false;
        }
      } catch (e) {
        h.history = h.history.filter(d => d !== today);
        h.completedToday = false;
        this.$toast?.error('Failed to update habit');
      } finally { this.savingId = null; }
    },

    // Toggle today's completion (🫡 ↔ 😑). Re-uses the per-day toggle endpoint
    // so the user can undo "today" without leaving the row.
    async toggleToday(h) {
      if (this.savingId) return;
      const today = todayISO();
      if (!isWorkday(today)) {
        this.$toast?.info('Habits are tracked on workdays only.');
        return;
      }
      this.savingId = h.id;
      try {
        await this.toggleHabitDate(h, today);
      } finally {
        this.savingId = null;
      }
    },

    // ── Habit detail / calendar dialog ────────────────────────────────
    openHabitDetail(h) {
      this.detailHabitId = h.id;
      this.detailDialog = true;
    },
    closeHabitDetail() {
      this.detailDialog = false;
      this.detailHabitId = null;
    },
    confirmDeleteFromDetail() {
      if (!this.detailHabit) return;
      const id = this.detailHabit.id;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this habit? This cannot be undone.')) return;
      this.removeHabit(id);
      this.closeHabitDetail();
    },

    // Per-day calendar cell helpers
    isWeekendIso(iso) { return !isWorkday(iso); },
    isFutureIso(iso)  { return iso > todayISO(); },
    isTodayIso(iso)   { return iso === todayISO(); },
    isDoneIso(h, iso) { return (h.history || []).includes(iso); },
    isCellDisabled(iso) { return this.isWeekendIso(iso) || this.isFutureIso(iso); },
    dayNumOf(iso)     { return Number(iso.split('-')[2]); },
    cellClass(h, iso) {
      const base = 'cal-cell';
      if (this.isWeekendIso(iso)) return `${base} cal-weekend`;
      if (this.isFutureIso(iso))  return `${base} cal-future`;
      if (this.isDoneIso(h, iso)) return `${base} cal-done`;
      if (this.isTodayIso(iso))   return `${base} cal-today`;
      return `${base} cal-missed`;
    },

    // Per-month status array for the year (used by the inline 12-month emoji strip)
    monthStatusesFor(h) {
      const target = this.habitTarget(h);
      const today = todayISO();
      const todayMonthKey = today.slice(0, 7); // YYYY-MM
      const history = new Set(h.history || []);
      const out = [];
      for (let m = 0; m < 12; m++) {
        const monthKey = `${this.curYear}-${String(m + 1).padStart(2, '0')}`;
        const totalDays = new Date(this.curYear, m + 1, 0).getDate();
        const workdays = [];
        for (let d = 1; d <= totalDays; d++) {
          const iso = `${this.curYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          if (isWorkday(iso)) workdays.push(iso);
        }
        const goal = Math.min(target, workdays.length);
        const elapsed = workdays.filter(d => d <= today).length;
        const done = workdays.filter(d => history.has(d)).length;

        let status;
        if (monthKey > todayMonthKey) {
          status = 'upcoming';
        } else if (elapsed === 0) {
          status = 'upcoming';
        } else if (done === 0) {
          status = 'inactive';
        } else if (monthKey === todayMonthKey) {
          if (done >= goal) status = 'excellent';
          else if (done === 0) {
            status = elapsed > 0 ? 'inactive' : 'upcoming';
          } else {
            const expected = (goal * elapsed) / workdays.length;
            // Lenient for in-progress month: ≥50% of expected pace = 'good'
            status = done >= expected * 0.5 ? 'good' : 'poor';
          }
        } else {
          // Past month
          if (done >= goal) status = 'excellent';
          else if (done >= goal * 0.6) status = 'good';
          else status = 'poor';
        }

        out.push({ status, done, goal, workdays: workdays.length });
      }
      return out;
    },
    statusEmojiUrl(status) {
      const base = '/emoji/';
      switch (status) {
        case 'excellent': return base + '1f60d.svg'; // 😍
        case 'good':      return base + '1f60a.svg'; // 😊
        case 'poor':      return base + '1f622.svg'; // 😢
        case 'inactive':  return base + '1f634.svg'; // 😴
        default:          return base + '1f634.svg';
      }
    },
    statusEmoji(status) {
      switch (status) {
        case 'excellent': return '😍';
        case 'good':      return '😊';
        case 'poor':      return '😢';
        case 'inactive':  return '😴';
        case 'upcoming':  return '🟡';
        default:          return '😴';
      }
    },
    monthStatusTooltip(ms) {
      if (ms.status === 'upcoming') return 'Upcoming';
      if (ms.status === 'inactive') return `Not updated · 0/${ms.goal} done`;
      return `${ms.done}/${ms.goal} done`;
    },
    headerMonthTooltip(m) {
      if (!m || m.total === 0) return 'No habits yet';
      const onTrack = (m.excellent || 0) + (m.good || 0);
      return `${onTrack}/${m.total} habits on track`;
    },

    // Time-of-day (AM / WORK / PM) toggle
    todValue(h) { return h.time_of_day || 'WORK'; },
    todClass(h) {
      const v = this.todValue(h);
      return v === 'AM' ? 'tod-am' : v === 'PM' ? 'tod-pm' : 'tod-work';
    },
    async cycleTimeOfDay(h) {
      const order = ['AM', 'WORK', 'PM'];
      const cur = this.todValue(h);
      const next = order[(order.indexOf(cur) + 1) % order.length];
      const prev = h.time_of_day || 'WORK';
      // Optimistic update
      this.$set(h, 'time_of_day', next);
      try {
        const res = await this.$axios.patch(
          `api/employee-growth/habits/${h.id}/time-of-day`,
          { time_of_day: next }
        );
        if (res.data?.response && res.data.data?.time_of_day) {
          this.$set(h, 'time_of_day', res.data.data.time_of_day);
        }
      } catch (e) {
        // Rollback on failure
        this.$set(h, 'time_of_day', prev);
        this.$toast?.error('Failed to update time of day');
      }
    },

    // Status emoji per habit for the CURRENT month
    monthStatusFor(h) {
      const target = this.habitTarget(h);
      const monthDays = this.currentMonthDaysList();
      const workdays = monthDays.filter(isWorkday);
      const today = todayISO();
      const elapsedWorkdays = workdays.filter(d => d <= today).length;
      const done = workdays.filter(d => (h.history || []).includes(d)).length;
      if (workdays.length === 0) return 'inactive';
      if (elapsedWorkdays === 0) return 'upcoming';
      if (done === 0) return 'inactive';
      const goal = Math.min(target, workdays.length);
      if (done >= goal) return 'excellent';
      const expected = (goal * elapsedWorkdays) / workdays.length;
      if (done >= expected * 0.95) return 'good';
      return 'poor';
    },
    currentMonthDaysList() {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const total = new Date(y, m + 1, 0).getDate();
      const out = [];
      for (let d = 1; d <= total; d++) {
        out.push(`${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
      }
      return out;
    },
    currentMonthEmojiFor(h) {
      const status = this.monthStatusFor(h);
      switch (status) {
        case 'excellent': return '😍';
        case 'good':      return '😊';
        case 'poor':      return '😢';
        case 'inactive':  return '😴';
        case 'upcoming':  return '🟡';
        default:          return '😴';
      }
    },
    statusChipLabel(h) {
      const map = {
        excellent: 'on track',
        good: 'barely',
        poor: 'behind',
        inactive: 'inactive',
        upcoming: 'upcoming',
      };
      return map[this.monthStatusFor(h)] || 'inactive';
    },
    statusChipClass(h) {
      const map = {
        excellent: 'chip-excellent',
        good: 'chip-good',
        poor: 'chip-poor',
        inactive: 'chip-inactive',
        upcoming: 'chip-upcoming',
      };
      return map[this.monthStatusFor(h)] || 'chip-inactive';
    },

    async toggleHabitDate(h, iso) {
      if (this.isCellDisabled(iso)) return;
      const had = (h.history || []).includes(iso);
      const today = todayISO();
      // Optimistic update
      const prevHistory = [...(h.history || [])];
      const prevCompletedToday = h.completedToday;
      h.history = had
        ? prevHistory.filter(d => d !== iso)
        : [...prevHistory, iso];
      if (iso === today) h.completedToday = !had;

      try {
        const res = await this.$axios.post(`api/employee-growth/habits/${h.id}/toggle-date`, { date: iso });
        if (res.data?.response) {
          const u = res.data.data;
          if (u) {
            if (Array.isArray(u.history)) h.history = u.history;
            if (typeof u.streak === 'number') h.streak = u.streak;
            if (typeof u.completed_today !== 'undefined') h.completedToday = !!u.completed_today;
          }
          this.$root.$emit('growth:refresh');
        } else {
          h.history = prevHistory;
          h.completedToday = prevCompletedToday;
          this.$toast?.error(res.data?.message || 'Failed to update');
        }
      } catch (e) {
        h.history = prevHistory;
        h.completedToday = prevCompletedToday;
        this.$toast?.error('Failed to update habit');
      }
    },
    async setHabitTarget(h, target) {
      const clamped = Math.max(1, Math.min(23, target));
      const prev = h.monthly_target;
      h.monthly_target = clamped;
      try {
        await this.$axios.patch(`api/employee-growth/habits/${h.id}/target`, { monthly_target: clamped });
      } catch (e) { h.monthly_target = prev; this.$toast?.error('Failed to update target'); }
    },
    async removeHabit(id) {
      try {
        await this.$axios.delete(`api/employee-growth/habits/${id}`);
        this.habits = this.habits.filter(h => h.id !== id);
      } catch (e) { this.$toast?.error('Failed to delete habit'); }
    },

    // ── AI suggestion ─────────────────────────────────────────────────
    normaliseHabit(text) {
      const stop = new Set(['a','an','the','do','daily','every','day','each','my','per','today','of','for','to','on','in','one','and','&','block','time']);
      return String(text||'').toLowerCase().replace(/[^a-z0-9\s]/g,' ').split(/\s+/).filter(w => w && !stop.has(w)).sort().join(' ');
    },
    isDuplicateHabit(candidate) {
      const norm = this.normaliseHabit(candidate);
      if (!norm) return true;
      return this.habits.some(h => {
        const existing = this.normaliseHabit(h.text);
        if (!existing) return false;
        if (existing === norm) return true;
        const a = new Set(existing.split(' ')), b = new Set(norm.split(' '));
        const shared = [...a].filter(t => b.has(t)).length;
        const minSize = Math.min(a.size, b.size);
        return minSize > 0 && shared / minSize >= 0.6;
      });
    },
    buildHabitSystemPrompt() {
      const profile = this.roleProfile, role = this.primaryRoleName;
      const habitLanes = {
        superadmin:'cross-functional rituals: daily team health check, weekly strategic review, decision log, unblock one team per day',
        management:'leadership rituals: daily unblock check with directs, weekly 1-on-1 prep, feedback-giving cadence, team metric review',
        sales:'pipeline discipline: daily prospecting block, end-of-day CRM hygiene, follow-up within 24h rule, weekly deal review',
        agent:'queue discipline: morning queue triage, SLA check 3x daily, end-of-day ticket wrap-up, weekly knowledge-base contribution',
        engineering:'shipping discipline: daily PR review block, test-first coding, deep-work focus block, weekly tech-debt item',
        marketing:'output discipline: daily analytics scan, weekly content ship, idea-capture journal, A/B test cadence',
        design:'craft discipline: daily design crit, shipping > polish rule, weekly portfolio capture, critique-giving cadence',
        recruitment:'pipeline discipline: daily candidate follow-ups, weekly sourcing block, pipeline health review',
        operations:'process discipline: daily inbox triage, SLA board review, weekly process improvement',
        finance:'close discipline: daily reconciliation block, weekly cash review, approval SLA, month-end prep cadence',
        generic:'focus discipline: daily planning, 1 deep-work block, end-of-day review, weekly reflection',
      };
      const lane = habitLanes[profile] || habitLanes.generic;
      const signals = [];
      if (this.overdueCount >= 3) signals.push(`- OVERDUE: ${this.overdueCount} overdue items → suggest a habit preventing overdue.`);
      else if (this.overdueCount === 0 && this.completedThisWeekCount >= 5) signals.push(`- HIGH MOMENTUM: ${this.completedThisWeekCount} done this week, 0 overdue → suggest a STRETCH habit.`);
      if (this.inProgressCount >= 4) signals.push(`- WIP OVERLOAD: ${this.inProgressCount} in-progress → suggest a "limit WIP" habit.`);
      if (this.habits.length === 0) signals.push(`- FIRST HABIT: simple and low-friction.`);
      else if (this.longestStreak >= 14) signals.push(`- STRONG TRACK RECORD: ${this.longestStreak}-day streak → raise the bar.`);
      else if (this.stalledHabits.length >= 2) signals.push(`- STRUGGLING: ${this.stalledHabits.length} habits at 0-streak → suggest something EASIER.`);
      if (this.managedRoleNames.length > 0) signals.push(`- MANAGES: ${this.managedRoleNames.join(', ')} team(s).`);
      const signalBlock = signals.length ? `\nPERFORMANCE SIGNALS:\n${signals.join('\n')}` : '';
      const existing = this.habits.map(h => `- ${h.text}`).join('\n') || '- (none yet)';
      return `You are a personal habit coach. Suggest ONE new daily habit.\n\nUSER ROLE: ${role} (profile: ${profile})\nHABIT LANES: ${lane}${signalBlock}\n\nEXISTING HABITS (do NOT overlap):\n${existing}\n\nRULES\n- Output ONLY the habit text. No quotes, no explanation.\n- Max 12 words. Specific and concrete.\n- Must be DIFFERENT from existing habits.\n- Must be do-able daily.`;
    },
    async addAiHabit() {
      if (this.aiSuggesting) return;
      this.aiSuggesting = true;
      try {
        const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthAreas });
        const system = this.buildHabitSystemPrompt() + '\n\nLIVE USER CONTEXT:\n' + userCtx;
        let habitText = '';
        for (let attempt = 0; attempt < 2; attempt++) {
          const userMsg = attempt === 0 ? 'Suggest my next daily habit.' : 'Your previous suggestion was too similar. Suggest a DIFFERENT habit.';
          const raw = await askAI(userMsg, system, { logSession: attempt === 0, isHighImpact: true });
          habitText = (raw||'').trim().replace(/^["'`]+|["'`]+$/g,'').replace(/^[-•*]\s*/,'').replace(/\s+/g,' ').slice(0, 120);
          if (habitText && !this.isDuplicateHabit(habitText)) break;
        }
        if (!habitText) throw new Error('empty');
        if (this.isDuplicateHabit(habitText)) { this.$toast?.info('AI could not find a fresh habit — try removing some existing ones first.'); return; }
        this.pendingHabit = habitText;
      } catch (e) { this.$toast?.error('Failed to get AI suggestion'); }
      finally { this.aiSuggesting = false; }
    },
    async confirmPendingHabit() {
      if (!this.pendingHabit) return;
      const habitText = this.pendingHabit;
      try {
        const res = await this.$axios.post('api/employee-growth/habits', { text: habitText, source: 'ai', monthly_target: this.defaultGoal });
        if (res.data.response) {
          const h = res.data.data;
          this.habits.push({ ...h, history: h.history||[], completedToday: !!h.completed_today, monthly_target: h.monthly_target||this.defaultGoal });
          this.pendingHabit = null;
        }
      } catch (e) { this.$toast?.error('Failed to add habit'); }
    },
    async regenPendingHabit() {
      if (this.aiSuggesting) return;
      this.pendingHabit = null; this.aiSuggesting = true;
      try {
        const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthAreas });
        const system = this.buildHabitSystemPrompt() + '\n\nLIVE USER CONTEXT:\n' + userCtx;
        let habitText = '';
        for (let attempt = 0; attempt < 2; attempt++) {
          const userMsg = attempt === 0 ? 'Suggest my next daily habit — different from before.' : 'Suggest a COMPLETELY DIFFERENT habit.';
          const raw = await askAI(userMsg, system, { logSession: false, isHighImpact: false });
          habitText = (raw||'').trim().replace(/^["'`]+|["'`]+$/g,'').replace(/^[-•*]\s*/,'').replace(/\s+/g,' ').slice(0, 120);
          if (habitText && !this.isDuplicateHabit(habitText)) break;
        }
        if (!habitText || this.isDuplicateHabit(habitText)) { this.$toast?.info('AI could not find a different habit.'); return; }
        this.pendingHabit = habitText;
      } catch (e) { this.$toast?.error('Failed to regenerate suggestion'); }
      finally { this.aiSuggesting = false; }
    },
  },
};
</script>

<style scoped>
.streak-ring-btn { transition: transform .15s; }
.streak-ring-btn:not([disabled]):hover { transform: scale(1.06); }
.streak-ring-btn[disabled] { opacity: .7; cursor: not-allowed; }

.completed-row { background: rgba(34,197,94,0.05); border: 1px solid rgba(34,197,94,0.3); }
.default-row   { background: rgba(0,0,0,0.03); border: 1px solid transparent; }
.default-row:hover { border-color: rgba(0,0,0,0.1); }

.mark-btn {
  font-weight: 600; border-radius: 6px; border: none; cursor: pointer;
  transition: all .15s; white-space: nowrap; outline: none;
}
.mark-btn[disabled] { cursor: not-allowed; opacity: .65; }
.mark-btn-compact  { font-size: 10px; padding: 3px 6px; }
.mark-btn-spacious { font-size: 11px; padding: 5px 10px; }
.mark-btn-todo   { background: linear-gradient(135deg,#6366f1,#8b5cf6); color: white; }
.mark-btn-todo:not([disabled]):hover { opacity: .88; transform: scale(1.05); }
.mark-btn-done   { background: rgba(34,197,94,0.12); color: #16a34a; border: 1px solid rgba(34,197,94,0.35) !important; }

.delete-btn { background: none; border: none; cursor: pointer; opacity: 0; padding: 2px; border-radius: 4px; transition: opacity .15s; }
.group-row:hover .delete-btn { opacity: 1; }

.combo-badge {
  display: inline-flex; align-items: center; font-size: 10px; font-weight: 600;
  border-radius: 9999px; overflow: hidden; border: 1px solid rgba(245,158,11,0.3);
}
.combo-left  { display: inline-flex; align-items: center; gap: 2px; padding: 2px 6px; background: rgba(245,158,11,0.1); color: #f59e0b; }
.combo-right { display: inline-flex; align-items: center; gap: 2px; padding: 2px 6px; }

.milestone-badge {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 10px; font-weight: 600;
  padding: 2px 6px; border-radius: 9999px; background: rgba(99,102,241,0.1); color: #6366f1;
}

.month-tier-legend { color: #f59e0b; font-weight: 900; }
.month-tier-elite  { color: #22c55e; font-weight: 700; }
.month-tier-strong { color: #f59e0b; font-weight: 700; }
.month-tier-building { color: #f59e0b; }

.month-bar-hit     { background: #22c55e; }
.month-bar-warn    { background: #f59e0b; }
.month-bar-partial { background: rgba(245,158,11,0.45); }
.month-bar-empty   { background: #d1d5db; }
.month-bar-future  { background: rgba(0,0,0,0.07); }

/* Status emoji button */
.emoji-status-btn { transition: transform .15s; user-select: none; }
.emoji-status-btn:hover { transform: scale(1.12); }

/* Habit name acts as a button */
.habit-name-btn {
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font-weight: 500;
  color: inherit;
  cursor: pointer;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.habit-name-btn:hover { color: #6366f1; }

/* Calendar dialog */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.weekday-label {
  flex: 1;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: rgba(0,0,0,0.5);
  text-transform: uppercase;
}
.cal-blank { aspect-ratio: 1 / 1; }
.cal-cell {
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background .15s, border-color .15s, transform .1s;
  color: rgba(0,0,0,0.7);
}
.cal-cell[disabled] { cursor: not-allowed; }
.cal-cell:not([disabled]):hover { transform: scale(1.05); }

.cal-weekend { background: rgba(0,0,0,0.02); color: rgba(0,0,0,0.3); }
.cal-future  { background: rgba(0,0,0,0.03); border-color: rgba(0,0,0,0.05); color: rgba(0,0,0,0.4); }
.cal-done    { background: rgba(34,197,94,0.18); border-color: rgba(34,197,94,0.5); color: #16a34a; }
.cal-done:hover { background: rgba(34,197,94,0.28); }
.cal-today   { background: rgba(99,102,241,0.15); border-color: #6366f1; color: #6366f1; }
.cal-today:hover { background: rgba(99,102,241,0.25); }
.cal-missed  { background: rgba(220,38,38,0.08); border-color: rgba(220,38,38,0.3); color: rgba(220,38,38,0.85); }
.cal-missed:hover { background: rgba(220,38,38,0.16); }

/* Streak / status chips inside calendar dialog */
.streak-chip {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 11px; font-weight: 600;
  padding: 2px 8px; border-radius: 9999px;
  background: rgba(245,158,11,0.15); color: #f59e0b;
  border: 1px solid rgba(245,158,11,0.35);
}
.status-chip {
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 600;
  padding: 2px 8px; border-radius: 9999px;
  border: 1px solid;
}
.chip-excellent { background: rgba(34,197,94,0.15);  color: #16a34a; border-color: rgba(34,197,94,0.4); }
.chip-good      { background: rgba(245,158,11,0.14); color: #b45309; border-color: rgba(245,158,11,0.38); }
.chip-poor      { background: rgba(220,38,38,0.12);  color: #dc2626; border-color: rgba(220,38,38,0.4); }
.chip-inactive  { background: rgba(0,0,0,0.05);      color: rgba(0,0,0,0.55); border-color: rgba(0,0,0,0.15); }
.chip-upcoming  { background: rgba(245,158,11,0.10); color: #b45309; border-color: rgba(245,158,11,0.3); }

/* 12-month emoji strip cells */
.month-emoji-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  width: 18px;
  height: 18px;
  user-select: none;
}
.hollow-circle {
  display: inline-block;
  border: 2px solid #f59e0b;
  border-radius: 50%;
  background: transparent;
}

/* Legend row (sits below header) */
.legend-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 10px;
  line-height: 1.4;
  color: rgba(0,0,0,0.55);
  padding: 4px 8px;
}
.legend-row > span { white-space: nowrap; }
.legend-row .default-goal {
  margin-left: auto;
  padding-left: 12px;
  border-left: 1px solid rgba(0,0,0,0.12);
}

/* AM / WORK / PM badge */
.tod-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  letter-spacing: 0.02em;
  transition: opacity .15s;
}
.tod-badge:hover { opacity: 0.8; }
.tod-am   { color: #6366f1; border-color: rgba(99,102,241,0.4);  background: rgba(99,102,241,0.1); }
.tod-work { color: #b45309; border-color: rgba(245,158,11,0.4);  background: rgba(245,158,11,0.12); }
.tod-pm   { color: rgba(0,0,0,0.55); border-color: rgba(0,0,0,0.18); background: rgba(0,0,0,0.04); }
</style>

